import { isFutureTimestamp } from './time-validation'

const CHANNEL = 'klines'
let lastFutureTimestampWarningAt = 0

type RealtimeBar = {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

type KlineUpdate = {
  symbol?: string
  interval?: string
  time?: number
  open?: number
  high?: number
  low?: number
  close?: number
  volume?: number
}

type ChannelMessage = {
  type?: string
  data?: KlineUpdate
}

export type StreamingConnection = {
  subscribeToChannelMessages(channel: string, options?: Record<string, unknown>): () => void
  addChannelHandler(channel: string, handler: (message: unknown) => void): () => void
}

export type StreamingConnectionProvider = () => Promise<StreamingConnection> | StreamingConnection

type StreamSubscription = {
  symbol: string
  interval: string
  listeners: Map<string, (bar: RealtimeBar) => void>
  unsubscribe?: () => void
  disposed: boolean
}

const RESOLUTION_TO_INTERVAL: Record<string, string> = {
  '1': '1m',
  '2': '2m',
  '3': '3m',
  '5': '5m',
  '10': '10m',
  '15': '15m',
  '30': '30m',
  '45': '45m',
  '60': '1h',
  '120': '2h',
  '180': '3h',
  '240': '4h',
  '360': '6h',
  '480': '8h',
  '720': '12h',
  D: '1d',
  '1D': '1d',
  W: '1w',
  '1W': '1w',
  M: '1M',
  '1M': '1M'
}

const subscriptions = new Map<string, StreamSubscription>()
const keyByListener = new Map<string, string>()

let connectionProvider: StreamingConnectionProvider | null = null
let connectionPromise: Promise<StreamingConnection> | null = null
let removeChannelHandler: (() => void) | null = null

const buildKey = (symbol: string, interval: string) => `${symbol}@${interval}`

export function setStreamingConnectionProvider(provider: StreamingConnectionProvider) {
  if (provider === connectionProvider) return

  connectionProvider = provider
  connectionPromise = null
  removeChannelHandler?.()
  removeChannelHandler = null
}

function isKlineUpdate(message: unknown): message is Required<Pick<ChannelMessage, 'data'>> {
  const candidate = message as ChannelMessage | null

  return Boolean(candidate && candidate.type === 'update' && candidate.data)
}

function handleChannelMessage(message: unknown) {
  if (!isKlineUpdate(message)) return

  const { symbol, interval, time } = message.data

  if (!symbol || !interval || typeof time !== 'number') return

  const subscription = subscriptions.get(buildKey(symbol, interval))

  if (!subscription) return

  const bar: RealtimeBar = {
    time: time * 1000,
    open: Number(message.data.open),
    high: Number(message.data.high),
    low: Number(message.data.low),
    close: Number(message.data.close),
    volume: Number(message.data.volume)
  }

  if (!Number.isFinite(bar.time) || isFutureTimestamp(bar.time)) {
    const now = Date.now()
    if (now - lastFutureTimestampWarningAt >= 30_000) {
      lastFutureTimestampWarningAt = now
      console.warn(`[udf] пропущена свеча с будущим временем: ${time}`)
    }
    return
  }

  subscription.listeners.forEach((onRealtimeCallback) => onRealtimeCallback(bar))
}

function getConnection(): Promise<StreamingConnection> {
  if (!connectionProvider) {
    return Promise.reject(
      new Error(
        'Streaming connection не задано. Вызовите setStreamingConnectionProvider() до создания датафида.'
      )
    )
  }

  if (!connectionPromise) {
    connectionPromise = Promise.resolve(connectionProvider())
      .then((connection) => {
        removeChannelHandler ??= connection.addChannelHandler(CHANNEL, handleChannelMessage)

        subscriptions.forEach((subscription) => {
          if (subscription.disposed || subscription.unsubscribe) return

          subscription.unsubscribe = connection.subscribeToChannelMessages(CHANNEL, {
            symbol: subscription.symbol,
            interval: subscription.interval
          })
        })

        return connection
      })
      .catch((error) => {
        connectionPromise = null
        throw error
      })
  }

  return connectionPromise
}

export function subscribeOnStream(
  symbolInfo: { ticker?: string; name?: string },
  resolution: string,
  onRealtimeCallback: (bar: RealtimeBar) => void,
  subscriberUID: string
) {
  const symbol = symbolInfo.ticker || symbolInfo.name
  const interval = RESOLUTION_TO_INTERVAL[resolution]

  if (!symbol || !interval) {
    console.warn(`[udf] нет подписки на реалтайм: symbol=${symbol}, resolution=${resolution}`)
    return
  }

  unsubscribeFromStream(subscriberUID)

  const key = buildKey(symbol, interval)
  let subscription = subscriptions.get(key)

  if (!subscription) {
    subscription = { symbol, interval, listeners: new Map(), disposed: false }
    subscriptions.set(key, subscription)

    const created = subscription

    void getConnection()
      .then((connection) => {
        if (created.disposed || created.unsubscribe) return

        created.unsubscribe = connection.subscribeToChannelMessages(CHANNEL, { symbol, interval })
      })
      .catch((error) => {
        console.error('[udf] не удалось подписаться на klines', error)
      })
  }

  subscription.listeners.set(subscriberUID, onRealtimeCallback)
  keyByListener.set(subscriberUID, key)
}

export function unsubscribeFromStream(subscriberUID: string) {
  const key = keyByListener.get(subscriberUID)

  if (!key) return

  keyByListener.delete(subscriberUID)

  const subscription = subscriptions.get(key)

  if (!subscription) return

  subscription.listeners.delete(subscriberUID)

  if (subscription.listeners.size) return

  subscription.disposed = true
  subscriptions.delete(key)
  subscription.unsubscribe?.()
}
